import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../config/api';
import { showError } from '../utils/toast';
import './Login.css';

// Eye icon SVG components
const EyeOpenIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeClosedIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      // Call the live API login endpoint
      const endpoint = '/admin/auth/login';
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://sayagainservices.sayagain.ai';
      console.log('🔐 Login Attempt:');
      console.log('  - Endpoint:', endpoint);
      console.log('  - API Base URL:', API_BASE_URL);
      console.log('  - Email:', data.email);
      
      const response = await apiClient.post(endpoint, {
        email: data.email,
        password: data.password
      }, null); // No token needed for login
      
      console.log('Login API Response:', response);
      console.log('Response type:', typeof response);
      console.log('Response keys:', Object.keys(response || {}));
      console.log('Full response structure:', JSON.stringify(response, null, 2));
      
      // Handle different response structures
      let token = null;
      let userData = null;
      
      // Try multiple possible token locations
      if (response.token) {
        // Response has token directly
        token = response.token;
        userData = response.user || response.data || { email: data.email };
        console.log('✅ Found token in response.token');
      } else if (response.data) {
        if (response.data.token) {
          token = response.data.token;
          userData = response.data.user || response.data || { email: data.email };
          console.log('✅ Found token in response.data.token');
        } else if (response.data.access_token) {
          token = response.data.access_token;
          userData = response.data.user || { email: data.email };
          console.log('✅ Found token in response.data.access_token');
        } else if (response.data.data && response.data.data.token) {
          token = response.data.data.token;
          userData = response.data.data.user || { email: data.email };
          console.log('✅ Found token in response.data.data.token');
        }
      } else if (response.access_token) {
        // Some APIs use access_token
        token = response.access_token;
        userData = response.user || { email: data.email };
        console.log('✅ Found token in response.access_token');
      } else if (response.result && response.result.token) {
        token = response.result.token;
        userData = response.result.user || { email: data.email };
        console.log('✅ Found token in response.result.token');
      } else if (response.body && response.body.token) {
        token = response.body.token;
        userData = response.body.user || { email: data.email };
        console.log('✅ Found token in response.body.token');
      }
      
      if (!token) {
        console.error('❌ Token not found. Response structure:', JSON.stringify(response, null, 2));
        throw new Error('Token not found in API response. Please check the API response structure.');
      }
      
      // Validate token before storing
      if (!token || typeof token !== 'string' || token.trim() === '') {
        console.error('❌ Invalid token received:', token);
        throw new Error('Invalid token received from server');
      }
      
      // Store token and user data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({
        id: userData.id || userData.user_id || 1,
        email: userData.email || data.email,
        type: 'admin'
      }));
      navigate('/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      
      // Extract error message from different possible structures
      let errorMessage = 'Login failed. Please check your credentials and try again.';
      
      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.error) {
        // Handle if err.error is a string or object
        errorMessage = typeof err.error === 'string' ? err.error : (err.error.message || JSON.stringify(err.error));
      } else if (err.data) {
        // Handle error data object
        if (typeof err.data === 'string') {
          errorMessage = err.data;
        } else if (err.data.error) {
          errorMessage = typeof err.data.error === 'string' ? err.data.error : err.data.error.message || JSON.stringify(err.data.error);
        } else if (err.data.message) {
          errorMessage = err.data.message;
        } else if (err.data.msg) {
          errorMessage = err.data.msg;
        } else {
          errorMessage = JSON.stringify(err.data);
        }
      } else if (err.response) {
        // Handle axios-like error structure
        if (err.response.data) {
          const data = err.response.data;
          errorMessage = data.error || data.message || data.msg || JSON.stringify(data);
        }
      }
      
      // Clean up error message - remove [object Object] if present
      if (errorMessage.includes('[object Object]')) {
        errorMessage = 'Invalid email or password. Please try again.';
      }
      
      // Handle specific HTTP status codes
      if (err.status === 401 || err.status === 403) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } else if (err.status === 404) {
        // Use the same API base URL logic as api.js
        const envUrl = process.env.REACT_APP_API_URL;
        let apiBase;
        if (envUrl) {
          let cleanUrl = envUrl.replace(/\/+$/, '');
          if (cleanUrl.endsWith('/api')) {
            apiBase = cleanUrl;
          } else {
            if (!cleanUrl.endsWith('/')) {
              cleanUrl = cleanUrl + '/';
            }
            apiBase = (cleanUrl + 'api').replace(/([^:]\/)\/+/g, '$1');
          }
        } else {
          apiBase = 'https://sayagainservices.sayagain.ai/api';
        }
        const fullUrl = `${apiBase}/admin/auth/login`.replace(/([^:]\/)\/+/g, '$1');
        errorMessage = `Login endpoint not found (404 Error).\n\nRequested URL: ${fullUrl}\n\nPossible solutions:\n1. Verify the login endpoint path on the API server\n2. Confirm the API URL is correct: ${envUrl || 'https://sayagainservices.sayagain.ai'}\n3. Check if the API server is running at ${envUrl || 'https://sayagainservices.sayagain.ai'}\n4. Contact the backend team for assistance`;
      } else if (err.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      // Show error only in toast, not in form
      if (errorMessage) {
        showError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-box">
        {/* Logo */}
        <div className="login-logo">
          <img src="/Sayagain.png" alt="SayAgain" style={{ height: '56px', width: 'auto' }} />
        </div>
        
        {/* Header */}
        <div className="login-header">
          <h1>Welcome back</h1>
          <p>Please enter your detail to sign in</p>
        </div>


        {/* OR Divider */}
        <div className="divider">
          <span>OR</span>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-groups">
            <label>E-Mail Address</label>
            <input
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              placeholder="Enter your email...."
              className={errors.email ? 'error' : ''}
            />
            {errors.email && (
              <span className="error-messages">{errors.email.message}</span>
            )}
          </div>
          
          <div className="form-groups">
            <label>Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                })}
                placeholder="Password@123"
                className={errors.password ? 'error' : ''}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
              </button>
            </div>
            {errors.password && (
              <span className="error-messages">{errors.password.message}</span>
            )}
          </div>

          {/* Remember me and Forgot password */}
          {/* <div className="form-options">
            <label className="remember-me">
              <input type="checkbox" />
              <span>Remember me</span>
            </label>
            <a href="#" className="forgot-password" onClick={(e) => e.preventDefault()}>
              Forgot password?
            </a>
          </div> */}

          <button type="submit" className="signin-btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

      </div>
    </div>
  );
};

export default Login;



